<script setup lang="ts">
/**
 * Zoekveld met vergrootglas/wis-icoon: het enige zoekveld (keuze klant 25/09/2026).
 *
 *   <VSearchInput v-model="term" @submit="zoek" />                  zoeken bij Enter
 *   <VSearchInput v-model="term" :debounce="500" @search="zoek" />   zoeken tijdens typen
 *
 * Vervangt BaseComponents/VSearchInput (`v-model:search` + `onsubmit`),
 * Components/Form/Search-Input en Portal/PortalSearchInput: allemaal dezelfde markup,
 * enkel het moment van zoeken verschilde. VFilterSearchInput is er een dunne laag op.
 *
 * Events:
 * - submit: Enter (het formulier rond het veld wordt tegengehouden).
 * - search: na typen, `debounce` ms na de laatste toets (0 = meteen); na wissen meteen.
 * - clear: het wis-icoon.
 *
 * Extra attributen (aria-*, role, data-*) gaan naar het <input> zelf en niet naar de
 * wrapper-div; `class`/`style` blijven op de wrapper. `size="lg"` = groter tekstveld
 * (command palette).
 */
import { IconSearch, IconX } from '@tabler/icons-vue'
import { debounce as lodashDebounce } from 'lodash'
import { computed, onBeforeUnmount, ref, useAttrs, type HTMLAttributes } from 'vue'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const modelValue = defineModel<string>({ default: '' })

const props = withDefaults(
    defineProps<{
        /** Vertaalsleutel. */
        placeholder?: string
        disabled?: boolean
        autofocus?: boolean
        /** Beide voorgangers zetten altijd `id="search"`; override bij meerdere per pagina. */
        id?: string
        /** Ms na de laatste toets voor het `search`-event (0 = meteen). */
        debounce?: number
        size?: 'md' | 'lg'
    }>(),
    {
        placeholder: 'global.search.fill_in_searchterm',
        disabled: false,
        autofocus: false,
        id: 'search',
        debounce: 0,
        size: 'md',
    }
)

const emit = defineEmits<{
    (e: 'submit'): void
    (e: 'search'): void
    (e: 'clear'): void
}>()

const emitSearch = lodashDebounce(() => emit('search'), props.debounce)
onBeforeUnmount(() => emitSearch.cancel())

function onInput(): void {
    if (props.debounce > 0) {
        emitSearch()
    } else {
        emit('search')
    }
}

// class/style horen bij de wrapper (breedte, marges); al het andere (aria-*, role, …)
// bij het invoerveld, anders zouden ARIA-attributen op een div zonder betekenis landen.
const wrapperAttrs = computed(() => ({ class: attrs.class, style: attrs.style }) as HTMLAttributes)
const inputAttrs = computed(() => {
    const { class: _class, style: _style, ...rest } = attrs
    return rest
})

const inputRef = ref<HTMLInputElement | null>(null)
defineExpose({ inputRef, focus: () => inputRef.value?.focus() })

const isSubmitting = ref(false)

/**
 * Enter houdt het formulier rond het veld tegen en emit `submit`. De vlag vangt een
 * dubbele Enter op vóór de navigatie gestart is.
 */
function handleSubmit(): void {
    if (isSubmitting.value) {
        return
    }

    isSubmitting.value = true
    // Enter zoekt meteen; een nog lopende debounce zou 500 ms later een tweede
    // zoekopdracht (tweede Inertia-visit) sturen.
    emitSearch.cancel()
    emit('submit')

    setTimeout(() => {
        isSubmitting.value = false
    }, 100)
}

/** Wissen zoekt meteen: niet wachten op de debounce voor een lege lijst terug. */
function clearSearch(): void {
    emitSearch.cancel()
    modelValue.value = ''
    emit('clear')
    emit('search')
}
</script>

<template>
    <div
        v-bind="wrapperAttrs"
        class="relative flex items-center has-[input:focus]:ring-offset-0 has-[input:focus]:ring-1 bg-white border border-gray-300 text-gray-900 text-sm rounded has-[input:focus]:ring-primary has-[input:focus]:shadow-primary dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:has-[input:focus]:ring-primary dark:has-[input:focus]:shadow-primary w-full md:min-w-[250px]"
    >
        <input
            ref="inputRef"
            v-bind="inputAttrs"
            type="search"
            autocomplete="off"
            v-model="modelValue"
            :placeholder="$t(props.placeholder)"
            :id="props.id"
            :disabled="props.disabled"
            :autofocus="props.autofocus"
            @input="onInput"
            @keydown.enter.prevent.stop="handleSubmit"
            class="block flex-grow w-full bg-transparent text-inherit ring-offset-0 ring-0 border-0 focus:ring-offset-0 focus:ring-0 focus:border-0"
            :class="props.size === 'lg' ? 'p-2 py-2.5 text-base' : 'p-2 text-sm'"
        />
        <span v-if="!modelValue" class="p-1.5 flex items-center justify-center" aria-hidden="true">
            <IconSearch stroke-width="1" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </span>
        <!-- Echte knop (was een klikbare span): ook met het toetsenbord te wissen. -->
        <button
            v-else
            type="button"
            class="p-1.5 flex items-center justify-center cursor-pointer rounded bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            :aria-label="$t('global.clear_search')"
            :disabled="props.disabled"
            @click="clearSearch()"
        >
            <IconX stroke-width="1" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
    </div>
</template>
